import { useTableController } from "@/components/datatable/controller"
import { DataTable } from "@/components/datatable/datatable"
import { ToolbarItem } from "@/components/datatable/toolbaritem"
import { Page, PageTitle } from "@/components/page"
import { usePage } from "@/components/page/hooks"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Progress } from "@/components/ui/progress"
import { ScrollArea } from "@/components/ui/scroll-area"
import { databricks } from "@/lib/agents/search/databricks"
import { documents } from '@/lib/agents/search/documents'
import { app } from "@/lib/app"
import { supabase } from "@/lib/data"
import { PaginationState } from "@tanstack/react-table"
import { BookCopy, Plus, Trash2 } from "lucide-react"
import { useRef, useState } from "react"
import { usePermissions } from "@/lib/hooks/usePermissions"
import { toast } from "sonner"
import { extractor } from "../vectors/extractor"
export const BUCKET = "docs"

export enum Statuses {
  Pending,
  Vectorized,
  Failed,
}

export const dbricks = {
  title: "Databricks",
  description: "Manage your organization's Documents.",
  route: "/docu/databricks",
  url: "/docu/databricks",
  icon: BookCopy,
  component,
  group: "docs",
  resource: "collections",
  action: "read",
} satisfies PageConfig


const acceptedFileTypes = Object.keys(documents.supportedExtensions).map(ext => `.${ext}`).join(",")


const columns: Columns<DatabricksTable> = {
  // id: { header: "id", size: 150 },
  file_type: { header: "Name", size: 500 },
  size: { header: "Estimated Tokens", size: 200, cell: DataTable.cellRender.number },
  // ingestion_timestamp: { header: "Created At", size: 180, cell: DataTable.cellRender.date },
  // box_path: { header: "Team", size: 180 },
}


interface FileToAdd {
  file: File
  url?: string
  name?: string
  error?: string
  progress?: number
  status?: 'pending' | 'uploading' | 'completed' | 'error'
}

function component() {

  // const [data, setData] = useState<DatabricksTable[]>([])
  const [version, setVersion] = useState(0)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const uploading = useRef(false)
  const [uploadProgress, setUploadProgress] = useState<FileToAdd[]>([])
  const [currentFileIndex, setCurrentFileIndex] = useState(0)
  const [selectedItems, setSelectedItems] = useState<DatabricksTable[]>([])
  const [showSummary, setShowSummary] = useState(false)
  const [uploadSummary, setUploadSummary] = useState<{ successful: number; errors: { file: string; error: string }[] }>({ successful: 0, errors: [] })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const ctrl = useTableController()
  const { team } = usePage()
  const { canCreate, canDelete } = usePermissions()
  const keys = useRef("")

  async function onLoad(p: PaginationState) {
    console.log(p)
    const apikeys = await app.fetchAPIkeys(team?.id)
    const r = await databricks.load({ keys: apikeys, team: team?.id })
    console.log("Result: ", r)
    return r
  }


  function onItemSelectionChange(selectedIds: DatabricksTable[]) {
    setSelectedItems(selectedIds)
  }


  async function onDeleteSelected() {
    const selected = ctrl.getSeleted() as DatabricksTable[]
    if (selected.length === 0) return
    ctrl.setLoading(true)
    const items = selected.map((item) => `${item.id}`)
    console.log("Delete selected:", items)
    const apiKeys = await app.fetchAPIkeys(team?.id)
    for (const item of items) {
      await databricks.delete({ id: item, token: apiKeys.databricks })
    }
    ctrl.setLoading(false)
    ctrl.reload()
  }

  async function onFileUpload(file: FileToAdd) {

    try {

      console.log(`Uploading file... ${file.name}`)

      const extension = file.name.split('.').pop()

      if (!documents.supportedExtensions[extension]) {
        console.log("Unsupported file type:", extension)
        file.error = "Unsupported file type"
        return
      }

      let ext: ExtractedDocument = {
        locale: "",
        text: "",
        chunks: [],
      }

      try {

        if (extension === "pdf") {
          ext = await extractor.extractPdf(file.file, keys.current)
        } else if (extension === "docx" || extension === "doc") {
          ext = await extractor.extractDoc(file.file, keys.current)
        } else if (extension === "xlsx" || extension === "csv" || extension === "xls") {
          ext = await extractor.extractExcel(file.file, keys.current)
        } else if (extension === "txt" || extension === "md" || extension === "json" || extension === "xml") {
          ext = await extractor.convertTextToMarkdown({ buffer: await file.file.arrayBuffer(), extension: extension as any, })
        } else if (extension === "jpg" || extension === "png" || extension === "webp" || extension === "gif") {
          ext = await extractor.extractImages(file.file, keys.current)
        }
      } catch (error) {
        console.log(error)
        file.error = error.toString()
        debugger
        return
      }

      if (ext.chunks.length == 0) ext.chunks.push(ext.text)

      const apikeys = await app.fetchAPIkeys(team?.id)

      for (const content of ext.chunks) {
        console.log("Should insert ", content)

        await databricks.insert({
          token: apikeys.databricks,
          team: team?.id,
          content,
          name: file.file.name,
          tokens: extractor.estimateTokenCount(content),
        })
      }

    } catch (error) {
      console.error("Error uploading file:", error)
      file.error = error.toString()
      debugger
    }

  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {

    if (!keys.current) {
      const apikeys = await app.fetchAPIkeys(team?.id)
      if (!apikeys || !apikeys.openai) {
        toast.error("OpenAI API key not found")
        return
      }
      keys.current = apikeys.openai
    }

    const files = event.target.files
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    const progressArray: FileToAdd[] = fileArray.map(file => ({
      file,
      progress: 0,
      status: 'pending' as const
    }))

    setUploadProgress(progressArray)
    setCurrentFileIndex(0)
    setIsDialogOpen(true)
    uploadFiles(progressArray)
  }

  const uploadFiles = async (files: FileToAdd[]) => {

    const apiKeys = await app.fetchAPIkeys(team?.id)
    const errors: { file: string; error: string }[] = []
    let successful = 0
    uploading.current = true

    try {
      for (let i = 0; i < files.length; i++) {
        const fileProgress = files[i]

        setCurrentFileIndex(i)
        setUploadProgress(prev => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'uploading', progress: 0 } : f
        ))
        try {
          const progressInterval = setInterval(() => {
            setUploadProgress(prev => prev.map((f, idx) => idx === i && f.progress < 90 ? { ...f, progress: f.progress + 10 } : f))
          }, 200)
          const toup: FileToAdd = {
            file: fileProgress.file,
            name: fileProgress.file.name
          }
          await onFileUpload(toup)
          clearInterval(progressInterval)

          if (toup.error) {
            errors.push({ file: fileProgress.file.name, error: toup.error })
            setUploadProgress(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'error', progress: 0, error: toup.error } : f))
          } else {
            successful++
            setUploadProgress(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'completed', progress: 100, url: toup.url } : f))
          }
        } catch (error) {
          const errorMsg = error?.toString() || 'Unknown error'
          errors.push({ file: fileProgress.file.name, error: errorMsg })
          setUploadProgress(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'error', progress: 0, error: errorMsg } : f))
        }
      }
    } catch (err) {
      console.log("Error during upload: ", err)

    } finally {
      uploading.current = false
    }

    await databricks.sync({ token: apiKeys.databricks })
    ctrl.reload()
    setIsDialogOpen(false)
    setUploadProgress([])
    if (fileInputRef.current) fileInputRef.current.value = ''
    setUploadSummary({ successful, errors })
    setShowSummary(true)
  }

  const handleCancel = () => {
    setIsDialogOpen(false)
    setUploadProgress([])
    setCurrentFileIndex(0)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return <Page>
    <div className="h-full grid grid-rows-[auto_1fr] gap-4">
      <div className="flex">
        <PageTitle />
        <div className="grow"></div>
        {canCreate("collections") && <div>
          <input ref={fileInputRef} type="file" accept={acceptedFileTypes} multiple onChange={handleFileSelect} className="hidden" />
          <Button className="rounded-lg" onClick={() => fileInputRef.current?.click()}>
            <Plus className="h-4 w-4" />Add Documents
          </Button>
        </div>}
      </div>


      <div className="h-full min-h-0 flex">
        <DataTable
          onLoad={onLoad}
          columns={columns}
          // onRowClick={onRowClick}
          onRowClick={"/docu/databricks"}
          onSelectedItemsChange={onItemSelectionChange}
          showSelection
          showSearch
          controller={ctrl}
        // loading={loading}
        >
          {canDelete("collections") && selectedItems.length > 0 &&
            <ToolbarItem>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Trash2 size={16} className="cursor-pointer hover:text-red-500" />
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete the documents.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onDeleteSelected} >Continue</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </ToolbarItem>}


        </DataTable>

      </div>
    </div>

    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Upload Documents</DialogTitle>
          <DialogDescription>
            {`Uploading file ${currentFileIndex + 1} of ${uploadProgress.length}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {uploadProgress[currentFileIndex] && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium truncate flex-1">{uploadProgress[currentFileIndex].file.name}</span>
                <span className="text-muted-foreground ml-2">
                  {uploadProgress[currentFileIndex].status === 'completed' ? '100%' :
                    uploadProgress[currentFileIndex].status === 'uploading' ? `${uploadProgress[currentFileIndex].progress}%` :
                      uploadProgress[currentFileIndex].status === 'error' ? 'Error' : 'Pending'}
                </span>
              </div>
              <Progress value={uploadProgress[currentFileIndex].progress} className="h-2" />
              {uploadProgress[currentFileIndex].error && (
                <div className="text-sm text-red-500 mt-1">{uploadProgress[currentFileIndex].error}</div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={showSummary} onOpenChange={setShowSummary}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Upload Summary</DialogTitle>
          <DialogDescription>
            Upload process completed
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex justify-between text-sm">
            <span className="font-medium">Successful uploads:</span>
            <span className="text-green-600">{uploadSummary.successful}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="font-medium">Failed uploads:</span>
            <span className="text-red-600">{uploadSummary.errors.length}</span>
          </div>

          {uploadSummary.errors.length > 0 && (
            <div className="space-y-2">
              <div className="font-medium text-sm">Errors:</div>
              <ScrollArea className="h-[200px] w-full rounded-md border p-4">
                <div className="space-y-3">
                  {uploadSummary.errors.map((error, index) => (
                    <div key={index} className="space-y-1">
                      <div className="font-medium text-sm">{error.file}</div>
                      <div className="text-sm text-red-500">{error.error}</div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={() => setShowSummary(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </Page >

}
